/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEventHandler, useCallback, useEffect, useRef} from 'react';
import Drawer from '@material-ui/core/Drawer';
import {makeStyles} from '@material-ui/core/styles';
import EntityLabel from '@ion/core/src/entities/EntityLabel';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {AccountLink} from '@ion/core/src/types/entities';
import {IconButton} from '@ion/dls/src/components/buttons/IconButton';
import {GlobalGroupChooserContent} from '@ion/dls/src/components/groupchooser/GlobalGroupChooserContent';
import {GroupChooserIcon} from '@ion/dls/src/icons/GroupChooserIcon';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import {dlsColor, dlsTypography, dlsZIndex} from '@ion/dls/src/theme/theme';
import {IconSize} from '@ion/dls/src/types/types';
import GlobalGroupsSearch from '@ion/features/src/global-groups/search';
import SkeletonLoader from '../loading/SkeletonLoader';
import {AutoSelectClient} from './GroupChooserAccordion';

const LOADER_HEIGHT = 16;
const LOADER_WIDTH = 80;

export interface GlobalGroupChooserProps extends AutoSelectClient {
  accountHierarchy: AccountLink[];
  selectedGroup: AccountLink;
  drawerOpen: boolean;
  includeSelectedAccountId?: boolean;
  includeBreadcrumbs?: boolean;
  hideParentAccount?: boolean;
  onDrawerClick: MouseEventHandler<HTMLDivElement>;
  onCloseDrawer: MouseEventHandler<HTMLButtonElement>;
  onSelectGroup: (value: AccountLink) => void;
  anchor?: 'bottom' | 'left' | 'right' | 'top';
  resetDeviceMovedAccount?: Function;
  hasAssetDeviceMove?: boolean;
  allCrumbsClickable?: boolean;
  onlyAccountId?: boolean;
}

const useStyles = makeStyles(theme => ({
  groupChooser : {
    padding      : '8px',
    borderRadius : '4px',
    '&:hover'    : {
      backgroundColor  : dlsColor('paleBlue'),
      backgroundRadius : '50%'
    }
  },
  groupChooserDisabled : {
    pointerEvents : 'none'
  },
  groupChooserButtonLabel : {
    ...dlsTypography('body1'),
    marginLeft : '4px',
    cursor     : 'pointer',
    whiteSpace : 'nowrap'
  },
  drawerRoot : {
    zIndex : `${dlsZIndex('globalGroupChooserDrawerRoot')} !important` as any
  },
  chooserIcon : {
    display        : 'flex',
    justifyContent : 'center'
  },
  groupChooserContent : {
    width    : '480px',
    padding  : '8px 18px 18px 36px',
    overflow : 'hidden'
  },
  closeIcon : {
    position  : 'absolute',
    marginTop : '5px'
  }
}));

/**
 * Desktop Global Group Chooser that, based on the account Hierarchy
 * passed to it will render a nested accordion with account options and
 * fire the passed callback when applied.
 *
 * ```typescript
 * import {GlobalGroupChooser} from '@ion/dls/src/components/groupchooser/GlobalGroupChooser';
 *
 *   <GlobalGroupChooser accountHierarchy={accountHierarchy}
 *     selectedGroup={globalGroup}
 *     drawerOpen={openChooser}
 *     onDrawerClick={onClick}
 *     onCloseDrawer={onCloseDrawer}
 *     onSelectGroup={selectGroup} />
 * ```
 *
 * @param accountHierarchy accountHierarchy that the accordion list will be built from
 * @param selectedGroup currently selected account/group
 * @param drawerOpen if the chooser drawer should be open or closed
 * @param onDrawerClick fires when drawer button is clicked
 * @param onCloseDrawer fires when drawer button is closed
 * @param onSelectGroup fires when user applies the changes in the form
 * @param resetDeviceMovedAccount fires to reset device moved account
 * @param hasAssetDeviceMove check whether asset device move is available or not
 *
 */
export const GlobalGroupChooser = ({
  accountHierarchy,
  selectedGroup,
  drawerOpen,
  onDrawerClick,
  onCloseDrawer,
  onSelectGroup,
  anchor = 'right',
  onlyAccountId = false,
  hideParentAccount = false,
  includeBreadcrumbs = false,
  resetDeviceMovedAccount,
  includeSelectedAccountId = false,
  hasAssetDeviceMove = false,
  allCrumbsClickable = false,
  disableAutoSelect = false
}: GlobalGroupChooserProps) => {
  const classes                = useStyles();
  const groupChooserContentRef = useRef(null);
  const title                  = EntityLabel.get(selectedGroup);
  const accountName            = selectedGroup && includeSelectedAccountId ? `${title}(${entityId(selectedGroup?.href)})` : title;

  useEffect(function() {
    GlobalGroupsSearch.setAccounts(accountHierarchy);
  }, [accountHierarchy, drawerOpen]);

  const onContentScrollToTop = useCallback(function() {
    groupChooserContentRef.current.scrollTo({
      top      : 0,
      behavior : 'smooth'
    });
  }, []);

  return (
    <>
      <div className={`${classes.groupChooser} ${!accountName && classes.groupChooserDisabled}`} onClick={onDrawerClick}>
        <div className={classes.chooserIcon}>
          <GroupChooserIcon />
        </div>
        <span className={classes.groupChooserButtonLabel}>
          {accountName || <SkeletonLoader width={`${LOADER_WIDTH}px`} height={`${LOADER_HEIGHT}px`} />}
        </span>
      </div>
      <Drawer open={drawerOpen}
        anchor={anchor}
        onClose={onCloseDrawer}
        classes={{
          root : classes.drawerRoot
        }}>
        <div className={classes.groupChooserContent} ref={groupChooserContentRef}>
          <div className={classes.closeIcon}>
            <IconButton icon={<MaterialExitCloseIcon size={IconSize.medium} color={dlsColor('grey3')} />}
              onClick={onCloseDrawer} />
          </div>
          {drawerOpen ? (
            <GlobalGroupChooserContent groups={accountHierarchy}
              onContentScrollToTop={onContentScrollToTop}
              currentGroup={selectedGroup}
              onGroupApply={onSelectGroup}
              drawerOpen={drawerOpen}
              onlyAccountId={onlyAccountId}
              hideParentAccount={hideParentAccount}
              includeBreadcrumbs={includeBreadcrumbs}
              resetDeviceMovedAccount={resetDeviceMovedAccount}
              hasAssetDeviceMove={hasAssetDeviceMove}
              allCrumbsClickable={allCrumbsClickable}
              disableAutoSelect={disableAutoSelect} />
          ) : undefined}
        </div>
      </Drawer>
    </>
  );
};

export default GlobalGroupChooser;
